import type { Key } from 'react';

/**
 * The keys a Columns menu offers, and the ones it must never offer, DERIVED from the column
 * array the page already has.
 *
 * ⚠️ THERE IS NO "PRIORITY METADATA", AND THIS IS WHAT STANDS IN FOR IT. The layered scheme
 * [P4-UX] describes shipped two of its three halves — a visible scroll edge and a pinned
 * actions column — and no per-column priority field was ever added (`grep -rn "priority"
 * web/src` finds nothing). What every column DOES carry is a stable `key` and a `title`, so
 * that is the metadata, and no page keeps a second list.
 *
 * ⚠️ THE LOCKS ARE DERIVED, NOT DECLARED, and that is the point. Both rules already exist in
 * CLAUDE.md §8 — "the first column of a list row OPENS that row" (`NameCell`), and an action
 * a scroll can hide is an action that is not there (the reason every actions column is
 * `fixed: 'right'`). A per-page `locked: ['name', 'actions']` would be a SECOND statement of
 * a rule the codebase already has, and the eleventh list page is the one that forgets it. A
 * derivation can be wrong for a whole class at once, which is a loud single-diff failure; a
 * declaration is wrong for exactly one page and nobody notices.
 */
const ACTIONS = /(^|-)actions$/;

export type ColumnMeta = { readonly key?: Key; readonly title?: unknown };

export type ColumnKeySet = { readonly keys: readonly string[]; readonly locked: readonly string[] };

export type ColumnKeySetOptions = {
  /**
   * ⚠️ ONE CALLER SETS THIS FALSE, and it needs the reason in writing: `HierarchyTable`
   * INJECTS the tree column and the row-actions column itself, so the array `pages/OrgTree`
   * hands the menu does not contain the row's first column at all. Locking index 0 there
   * would lock `Type` for no reason and silently retire a column that has been hideable
   * since P1a.2.
   */
  readonly lockFirst?: boolean;
  /** Adds to the derived floor. It can never lower it. */
  readonly alsoLocked?: readonly string[];
};

export const columnKeySet = (columns: readonly ColumnMeta[], options: ColumnKeySetOptions = {}): ColumnKeySet => {
  const { lockFirst = true, alsoLocked = [] } = options;
  const keys = columns.map((column) => String(column.key ?? ''));
  const locked = keys.filter(
    (key, index) =>
      // A column with no key cannot be addressed by the URL, so it can never be hidden.
      key === '' || (lockFirst && index === 0) || ACTIONS.test(key) || alsoLocked.includes(key),
  );
  return { keys, locked };
};
